import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Code,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import QRCode from "react-qr-code";
import { EventsApi, type EventRow, type EventPatch, type Pricing } from "../api/events";
import { customerBaseUrl } from "../auth/authConfig";
import { getEnv } from "../hooks/useEnv";
import { centsToUsd } from "../lib/money";
import { PricingFields } from "./PricingFields";
import { ReferralTermsFields } from "./ReferralTermsFields";
import logger from "../analytics/logger";

function eq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// Renders the QR as a rasterized PNG <img> (not the raw SVG) so the browser's
// right-click "Copy image" / "Save image as…" produces a real PNG. react-qr-code
// draws the SVG into a hidden holder; we serialize it, paint it onto a canvas,
// and export image/png.
function QrPng({ value, size = 180 }: { value: string; size?: number }) {
  const holderRef = useRef<HTMLDivElement>(null);
  const [png, setPng] = useState<string | null>(null);

  useEffect(() => {
    const svg = holderRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      setPng(canvas.toDataURL("image/png"));
    };
    img.src = src;
  }, [value, size]);

  return (
    <>
      <Box ref={holderRef} position="absolute" left="-9999px" top="-9999px" aria-hidden>
        <QRCode size={size} value={value} bgColor="#ffffff" fgColor="#000000" viewBox={`0 0 ${size} ${size}`} />
      </Box>
      {png ? (
        <img src={png} width={size} height={size} alt="Event campaign QR code" />
      ) : (
        <Box width={`${size}px`} height={`${size}px`} />
      )}
    </>
  );
}

// <input type="date"> wants YYYY-MM-DD; the API returns a full ISO datetime.
function toDateInput(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

// <input type="datetime-local"> wants a bare "YYYY-MM-DDTHH:mm" with no
// offset, but the API returns (and requires, for the house-default window) an
// offset-bearing ISO datetime. Do NOT reuse toDateInput — it truncates to a
// date, which cannot express a precise boundary.
//
// datetime-local always shows/accepts the browser's own wall clock (it has no
// offset control), so toDatetimeLocalInput reads the stored instant back out
// using local getters — that's what keeps the field showing the same time an
// operator typed instead of jumping by the browser's UTC offset on redisplay.
// toIsoFromDatetimeLocal does the send-side conversion: `new Date(localValue)`
// parses the bare string as local time (the behavior an operator expects —
// they typed their own clock), and `.toISOString()` re-emits it as an
// offset-bearing UTC string, which is what the backend's aware-datetime check
// requires. The resolved UTC instant is rendered separately, next to the
// field, so the conversion is verifiable rather than implied.
function toDatetimeLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIsoFromDatetimeLocal(local: string | null): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function buildPatch(orig: EventRow, draft: EventRow): EventPatch {
  const patch: EventPatch = {};
  if (!eq(orig.name, draft.name)) patch.name = draft.name;
  if (!eq(orig.slug, draft.slug)) patch.slug = draft.slug;
  if (!eq(toDateInput(orig.event_date), toDateInput(draft.event_date)))
    patch.event_date = draft.event_date;
  if (!eq(toDateInput(orig.event_end_date), toDateInput(draft.event_end_date)))
    patch.event_end_date = draft.event_end_date;
  if (!eq(orig.house_default_start_at, draft.house_default_start_at))
    patch.house_default_start_at = draft.house_default_start_at;
  if (!eq(orig.house_default_end_at, draft.house_default_end_at))
    patch.house_default_end_at = draft.house_default_end_at;
  if (!eq(orig.location, draft.location)) patch.location = draft.location;
  // Pricing: an all-null object means "revert to inheriting the origin Default",
  // so normalize it to null before diffing. Only send pricing when it changed.
  const dp = draft.pricing;
  const allNull =
    !dp ||
    (dp.creator_verification == null &&
      dp.work_certification == null &&
      dp.free_works == null);
  const normalized = allNull ? null : dp;
  if (!eq(orig.pricing, normalized)) patch.pricing = normalized;
  if (!eq(orig.referral_terms, draft.referral_terms))
    patch.referral_terms = draft.referral_terms;
  return patch;
}

export default function EventEditModal({
  event,
  isOpen,
  onClose,
  onSaved,
}: {
  event: EventRow | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<EventRow | null>(event);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reseed the draft whenever a different event is opened.
  const [prevEvent, setPrevEvent] = useState(event);
  if (event !== prevEvent) {
    setPrevEvent(event);
    setDraft(event);
    setError(null);
  }

  if (!event || !draft) return null;

  // The origin's baseline event is keyed by this exact name; renaming it would
  // orphan the baseline, so the name is read-only for the Default event.
  const isDefault = event.name === "Default";

  // The Default event links to the bare origin slug (no ?code=); a specific
  // event carries its id as the campaign code.
  const url =
    event.campaign_url ??
    (isDefault
      ? `${customerBaseUrl(getEnv())}/${event.origin_slug ?? ""}`
      : `${customerBaseUrl(getEnv())}/${event.origin_slug ?? ""}?code=${event.id}`);

  // When a slug is set, offer a shorter, memorable URL alongside the permanent
  // id-based one. The slug is mutable (renaming breaks old slug links → they fall
  // back to General), so the QR/primary URL above intentionally stay on the id.
  // Sourced from the backend (server-side CUSTOMER_BASE_URL) so it matches the
  // environment, exactly like campaign_url — the client customerBaseUrl helper is
  // intentionally always-prod and would mis-render dev as www.
  const shortUrl = event.short_url;

  const eff = event.effective_pricing;
  const inheritHint =
    event.pricing_inherited && eff
      ? `Inheriting origin Default — verification $${centsToUsd(eff.creator_verification)}, certification $${centsToUsd(eff.work_certification)}${eff.work_certification === 0 ? "" : `, free works ${eff.free_works ?? "—"}`}. Enter values to override; leave blank to keep inheriting.`
      : null;

  const copyText = async (text: string, title: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ status: "success", title, duration: 2000 });
    } catch (e) {
      logger.error("event_copy_url_failed", e);
      toast({ status: "error", title: "Copy failed", duration: 2500 });
    }
  };

  const save = async () => {
    const patch = buildPatch(event, draft);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await EventsApi.update(event.id, patch);
      toast({ status: "success", title: "Event saved", duration: 1500 });
      onSaved();
    } catch (e: unknown) {
      logger.error("event_update_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to save changes";
      const friendly: Record<string, string> = {
        house_default_unpriced:
          "This event has no effective pricing. Set pricing before making it the house default — otherwise new signups get no starter credits.",
        house_default_overlaps:
          "Another event is already the house default for part of that window.",
        invalid_house_default_window:
          "Start must be before end, and both must be set together (or both cleared).",
        referral_terms_required_for_general_default:
          "General/Default is the referral-terms cascade floor and can't be cleared — every referral link falls back to it.",
        referral_terms_conflict:
          "Someone else just changed this event's referral terms. Reload and try again.",
        referrer_credits_required: "Referrer credits is required.",
        referrer_credits_must_be_integer: "Referrer credits must be a whole number.",
        referrer_credits_must_be_non_negative: "Referrer credits can't be negative.",
        referee_bonus_works_required: "Referred creator bonus works is required.",
        referee_bonus_works_must_be_integer:
          "Referred creator bonus works must be a whole number.",
        referee_bonus_works_must_be_non_negative:
          "Referred creator bonus works can't be negative.",
      };
      setError(friendly[msg] ?? msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{event.name || "(unnamed event)"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Stack spacing={4}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Origin
              </Text>
              <Text>
                {event.display_name
                  ? `${event.display_name} (${event.origin_slug})`
                  : event.origin_slug ?? "—"}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Name
              </Text>
              <Input
                size="sm"
                value={draft.name ?? ""}
                onChange={(e) => setDraft({ ...draft, name: e.target.value || null })}
                isReadOnly={isDefault}
                isDisabled={isDefault}
              />
              {isDefault && (
                <Text fontSize="xs" color="gray.400" mt={1}>
                  This is the origin's baseline event — its name can't be changed.
                </Text>
              )}
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Slug (optional)
              </Text>
              <Input
                size="sm"
                value={draft.slug ?? ""}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value || null })}
                placeholder="e.g. summer-2026"
              />
              <Text fontSize="xs" color="gray.400" mt={1}>
                Short campaign code — letters, numbers, hyphens; auto-normalized on save
                (e.g. "Summer 2026" → summer-2026). Renaming it breaks links using the old
                slug (they fall back to General).
              </Text>
            </Box>
            <HStack align="flex-start" spacing={4}>
              <Box flex={1}>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  Start date
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={toDateInput(draft.event_date)}
                  onChange={(e) => setDraft({ ...draft, event_date: e.target.value || null })}
                />
              </Box>
              <Box flex={1}>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  End date (optional)
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={toDateInput(draft.event_end_date)}
                  onChange={(e) =>
                    setDraft({ ...draft, event_end_date: e.target.value || null })
                  }
                />
              </Box>
            </HStack>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Location
              </Text>
              <Input
                size="sm"
                value={draft.location ?? ""}
                onChange={(e) => setDraft({ ...draft, location: e.target.value || null })}
              />
            </Box>
            <Box>
              <Text fontWeight="semibold" mb={1}>
                House default window
              </Text>
              <Text fontSize="xs" color="gray.500" mb={2}>
                While this window is live, signups that arrive with no origin at all are
                attributed to this event instead of General's Default event.
              </Text>
              <HStack align="flex-start" spacing={4}>
                <Box flex={1}>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Start ({browserTimeZone})
                  </Text>
                  <Input
                    size="sm"
                    type="datetime-local"
                    value={toDatetimeLocalInput(draft.house_default_start_at)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        house_default_start_at: toIsoFromDatetimeLocal(e.target.value || null),
                      })
                    }
                  />
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Resolves to{" "}
                    {draft.house_default_start_at
                      ? new Date(draft.house_default_start_at).toISOString().slice(0, 16) + " UTC"
                      : "—"}
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    End ({browserTimeZone})
                  </Text>
                  <Input
                    size="sm"
                    type="datetime-local"
                    value={toDatetimeLocalInput(draft.house_default_end_at)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        house_default_end_at: toIsoFromDatetimeLocal(e.target.value || null),
                      })
                    }
                  />
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Resolves to{" "}
                    {draft.house_default_end_at
                      ? new Date(draft.house_default_end_at).toISOString().slice(0, 16) + " UTC"
                      : "—"}
                  </Text>
                </Box>
              </HStack>
            </Box>
            <Box>
              <Text fontWeight="semibold" mb={1}>
                Pricing
              </Text>
              {inheritHint && (
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {inheritHint}
                </Text>
              )}
              <PricingFields
                value={draft.pricing}
                onChange={(p: Pricing) => setDraft({ ...draft, pricing: p })}
                editing
              />
            </Box>
            <Box>
              <Text fontWeight="semibold" mb={1}>
                Referral terms
              </Text>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Credits paid to whoever refers a signup through this event, and the bonus
                free works granted to the referred creator. Leave unset to inherit from the
                origin, then General/Default.
              </Text>
              <ReferralTermsFields
                value={draft.referral_terms}
                onChange={(rt) => setDraft({ ...draft, referral_terms: rt })}
                editing
                clearDisabledReason={
                  event.is_general_default
                    ? "General/Default is the cascade floor and can't be left unconfigured."
                    : null
                }
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Campaign QR code
              </Text>
              <Box p={3} bg="white" display="inline-block" borderWidth="1px" borderRadius="md">
                <QrPng value={url} size={180} />
              </Box>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Campaign URL
              </Text>
              <HStack align="flex-start">
                <Code fontSize="sm" p={2} borderRadius="md" wordBreak="break-all">
                  {url}
                </Code>
                <Tooltip label="Copy campaign URL">
                  <IconButton
                    aria-label="Copy campaign URL"
                    icon={<CopyIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => copyText(url, "Copied campaign URL")}
                  />
                </Tooltip>
              </HStack>
            </Box>
            {shortUrl && (
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  Short URL
                </Text>
                <HStack align="flex-start">
                  <Code fontSize="sm" p={2} borderRadius="md" wordBreak="break-all">
                    {shortUrl}
                  </Code>
                  <Tooltip label="Copy short URL">
                    <IconButton
                      aria-label="Copy short URL"
                      icon={<CopyIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => copyText(shortUrl, "Copied short URL")}
                    />
                  </Tooltip>
                </HStack>
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
